// src/views/bots/flow/utils/flowConverter.ts
// Utilities for converting between backend groups format and React Flow format

import { Node, Edge } from '@xyflow/react';
import { BotGroup, BotStep, QuestionNodeData } from '../types/flow.types';
import logger from '../../../../utils/logger';

export interface TriggerCondition {
  keywords: string[];
}

/**
 * Convert backend groups to React Flow nodes and edges
 */
export function convertGroupsToFlow(
  groups: BotGroup[],
  triggerCondition?: TriggerCondition
): { nodes: Node[]; edges: Edge[] } {
  logger.log('[FlowConverter] Converting groups to flow', {
    groupCount: groups.length,
  });

  const nodes: Node[] = [];
  const edges: Edge[] = [];
  const nodeIdMap = new Map<string, string>(); // stepId -> nodeId
  const nodePositions = new Map<string, { x: number; y: number }>(); // nodeId -> position
  const processedSteps = new Set<string>(); // Track which steps we've positioned

  // Create START node
  const startNode: Node = {
    id: 'start',
    type: 'startNode',
    position: { x: 400, y: 50 },
    data: {
      keywords: triggerCondition?.keywords || [],
    },
  };
  nodes.push(startNode);

  // ✅ FIXED: Use step.id directly - no transformation
  // First pass: Create node ID mapping and build all nodes
  const allSteps: Array<{ group: BotGroup; step: BotStep; stepIndex: number }> = [];

  groups.forEach((group) => {
    group.steps.forEach((step, stepIndex) => {
      const nodeId = step.id; // ✅ Use step.id directly - preserve frontend IDs
      nodeIdMap.set(step.id, nodeId);
      allSteps.push({ group, step, stepIndex });
    });
  });

  // Second pass: Layout nodes with smart positioning for branches
  let currentY = 200; // Start Y position
  const X_SPACING = 400; // Horizontal spacing between levels
  const Y_BRANCH_SPACING = 300; // Vertical spacing between branches
  const Y_SEQUENTIAL_SPACING = 150; // Vertical spacing within same group

  // Start with first group
  if (groups.length > 0) {
    const MAX_LAYOUT_DEPTH = 20; // Prevent stack overflow on circular data
    const layoutGroup = (
      group: BotGroup,
      startX: number,
      startY: number,
      isFirstGroup: boolean = false,
      depth: number = 0
    ): number => {
      if (depth > MAX_LAYOUT_DEPTH) {
        logger.warn('[FlowConverter] Max layout depth reached — possible circular reference');
        return startY;
      }
      let maxY = startY;

      group.steps.forEach((step, stepIndex) => {
        const nodeId = step.id; // ✅ Use step.id directly - preserve frontend IDs

        // Skip if already positioned
        if (processedSteps.has(step.id)) {
          return;
        }
        processedSteps.add(step.id);

        // Calculate position
        const yPosition = startY + (stepIndex * Y_SEQUENTIAL_SPACING);
        nodePositions.set(nodeId, { x: startX, y: yPosition });
        maxY = Math.max(maxY, yPosition);

        // Transform fieldType
        let nodeType = step.fieldType || 'text';
        if (step.fieldType === 'interactive' && step.interactive?.type) {
          nodeType = `interactive-${step.interactive.type}`;
        }

        // For interactive types, body text is in interactive.body.text, not message
        const questionText = step.message || (step.interactive as any)?.body?.text || '';

        // For template steps, read templateId from step.template (backend top-level field)
        const templateId = (step as any).template || step.metadata?.templateId || '';
        const templateData = step.fieldType === 'template' && templateId
          ? { templateId, templateName: step.metadata?.templateName || '', template: null }
          : undefined;

        const node: Node = {
          id: nodeId,
          type: nodeType,
          position: { x: startX, y: yPosition },
          data: {
            questionType: nodeType,
            question: questionText,
            required: step.validation?.required || group.required || false,
            interactive: step.interactive,
            validation: step.validation,
            metadata: step.metadata,
            options: step.options,
            groupId: group.id,
            groupName: group.groupName,
            stepId: step.id,
            ...(templateData ? { templateData } : {}),
          },
        };

        nodes.push(node);

        // Handle branching conditions
        if (step.conditions && step.conditions.length > 0) {
          let branchY = yPosition;

          step.conditions.forEach((condition, conditionIndex) => {
            const targetStepId = (condition as any).targetStep;

            // Find the target step and its group
            const targetStepInfo = allSteps.find(s => s.step.id === targetStepId);

            if (targetStepInfo) {
              const targetGroup = targetStepInfo.group;

              // Position branch groups vertically spaced
              const branchX = startX + X_SPACING;
              const branchStartY = branchY + (conditionIndex * Y_BRANCH_SPACING);

              // Layout the target group if not processed
              if (!processedSteps.has(targetStepInfo.step.id)) {
                const groupEndY = layoutGroup(targetGroup, branchX, branchStartY, false, depth + 1);
                maxY = Math.max(maxY, groupEndY);
              }
            }
          });
        }
      });

      return maxY;
    };

    // Start layout from first group
    layoutGroup(groups[0], 100, currentY, true);
  }

  // Third pass: Create edges based on conditions, metadata.nextStep, and sequential fallback
  // ✅ FIX: Collect all branch target step IDs to avoid wrong sequential edges
  const branchTargetStepIds = new Set<string>();
  groups.forEach((group) => {
    group.steps.forEach((step) => {
      if (step.conditions && step.conditions.length > 0) {
        step.conditions.forEach((condition) => {
          const targetStepId = (condition as any).targetStep;
          if (targetStepId) {
            branchTargetStepIds.add(targetStepId);
          }
        });
      }
    });
  });

  logger.log('[FlowConverter] Branch target steps:', Array.from(branchTargetStepIds));

  // Track which nodes already have an outgoing edge (to avoid duplicates)
  const nodesWithOutgoingEdge = new Set<string>();

  groups.forEach((group) => {
    group.steps.forEach((step, stepIndex) => {
      const nodeId = step.id;

      // Create edges from conditions (branching)
      if (step.conditions && step.conditions.length > 0) {
        step.conditions.forEach((condition) => {
          const targetStepId = (condition as any).targetStep;
          const targetNodeId = nodeIdMap.get(targetStepId);

          if (targetNodeId) {
            let options: any[] = [];
            const isInteractive = step.fieldType === 'interactive';
            const interactiveType = step.interactive?.type;

            if (isInteractive && interactiveType === 'button') {
              options = step.interactive?.action?.buttons?.map((btn: any) => ({
                id: btn.reply.id,
                title: btn.reply.title,
              })) || [];
            } else if (isInteractive && interactiveType === 'list') {
              step.interactive?.action?.sections?.forEach((section: any) => {
                section.rows?.forEach((row: any) => {
                  options.push({ id: row.id, title: row.title });
                });
              });
            }

            const option = options.find((opt: any) => opt.title === condition.value);
            const optionId = option?.id || condition.value;

            const edge: Edge = {
              id: `edge-${nodeId}-${targetNodeId}-${optionId}`,
              source: nodeId,
              target: targetNodeId,
              sourceHandle: `branch-${optionId}`,
              type: 'smoothstep',
              animated: false,
              label: condition.value,
              style: { stroke: '#94a3b8', strokeWidth: 3 },
              labelStyle: { fill: '#111b21', fontWeight: 500, fontSize: 13 },
              labelBgStyle: { fill: 'white', fillOpacity: 0.95 },
            };

            edges.push(edge);
            nodesWithOutgoingEdge.add(nodeId);
          }
        });
      }

      // ✅ FIX: Create edge from metadata.nextStep (saved sequential connection)
      const nextStepFromMetadata = (step as any).metadata?.nextStep;
      if (nextStepFromMetadata) {
        const targetNodeId = nodeIdMap.get(nextStepFromMetadata);
        if (targetNodeId) {
          const edge: Edge = {
            id: `edge-${nodeId}-${targetNodeId}-seq`,
            source: nodeId,
            target: targetNodeId,
            sourceHandle: 'question-output',
            type: 'smoothstep',
            animated: false,
            style: { stroke: '#94a3b8', strokeWidth: 3 },
          };
          edges.push(edge);
          nodesWithOutgoingEdge.add(nodeId);
          logger.log('[FlowConverter] Created edge from metadata.nextStep:', nodeId, '->', targetNodeId);
        }
      }

      // ✅ FALLBACK: Only for LEGACY data where metadata.nextStep was never saved.
      // If nextStep is explicitly null, the user intentionally disconnected — don't recreate.
      // If nextStep is undefined (missing from metadata), it's old data — use group order as fallback.
      const hasExplicitNextStep = step.metadata && 'nextStep' in step.metadata;
      if (!nodesWithOutgoingEdge.has(nodeId) && !hasExplicitNextStep) {
        const nextStep = group.steps[stepIndex + 1];
        if (nextStep) {
          const targetNodeId = nodeIdMap.get(nextStep.id);
          if (targetNodeId) {
            const edge: Edge = {
              id: `edge-${nodeId}-${targetNodeId}-fallback`,
              source: nodeId,
              target: targetNodeId,
              sourceHandle: 'question-output',
              type: 'smoothstep',
              animated: false,
              style: { stroke: '#94a3b8', strokeWidth: 3 },
            };
            edges.push(edge);
            nodesWithOutgoingEdge.add(nodeId);
            logger.log('[FlowConverter] Created fallback sequential edge:', nodeId, '->', targetNodeId);
          }
        }
      }
    });
  });

  // Connect START to first group's first step
  if (groups.length > 0 && groups[0].steps.length > 0) {
    const firstStepId = groups[0].steps[0].id;
    const firstNodeId = firstStepId; // ✅ Use step.id directly - preserve frontend IDs

    const startEdge: Edge = {
      id: `edge-start-${firstNodeId}`,
      source: 'start',
      target: firstNodeId,
      sourceHandle: 'start-output',
      type: 'smoothstep',
      animated: false,
      style: {
        stroke: '#94a3b8',
        strokeWidth: 3,
      },
    };

    edges.push(startEdge);
  }

  logger.log('[FlowConverter] Conversion complete', {
    nodeCount: nodes.length,
    edgeCount: edges.length,
  });

  return { nodes, edges };
}

/**
 * Convert React Flow nodes and edges back to backend groups format
 */
export function convertFlowToGroups(
  nodes: Node[],
  edges: Edge[]
): { groups: BotGroup[]; triggerCondition: TriggerCondition } {
  logger.log('[FlowConverter] Converting flow to groups', {
    nodeCount: nodes.length,
    edgeCount: edges.length,
  });

  // Extract START node keywords
  const startNode = nodes.find((n) => n.type === 'startNode');
  const triggerCondition: TriggerCondition = {
    keywords: (startNode?.data.keywords as string[]) || [],
  };

  // Group nodes by their groupId or create new groups
  const groupsMap = new Map<string, BotStep[]>();
  const nodeToGroupMap = new Map<string, string>();

  // Question nodes (exclude START - no END nodes anymore)
  const questionNodes = nodes.filter(
    (n) => n.type !== 'startNode'
  );

  // If nodes have groupId, use it; otherwise create groups based on flow
  questionNodes.forEach((node) => {
    const groupId = (node.data.groupId as string) || 'group-1';
    nodeToGroupMap.set(node.id, groupId);

    if (!groupsMap.has(groupId)) {
      groupsMap.set(groupId, []);
    }

    // Build conditions from outgoing edges (only for branching types)
    const outgoingEdges = edges.filter((e) => e.source === node.id);
    const conditions: Array<{ value: string; operator?: string; targetStep: string }> = [];

    // ✅ FIX: Track sequential next step for non-branching edges
    let nextStepId: string | null = null;
    const sequentialEdge = outgoingEdges.find((e) => e.sourceHandle === 'question-output');
    if (sequentialEdge) {
      const targetNode = nodes.find((n) => n.id === sequentialEdge.target);
      nextStepId = (targetNode?.data.stepId as string) || targetNode?.id || null;
    }

    const isBranching = node.type === 'interactive-button' || node.type === 'interactive-list';

    if (isBranching) {
      // Extract button/list options to map ID → Title
      let optionTitleMap = new Map<string, string>();

      if (node.type === 'interactive-button') {
        const buttons = (node.data.interactive as any)?.action?.buttons || [];
        buttons.forEach((btn: any) => {
          optionTitleMap.set(btn.reply.id, btn.reply.title);
        });
      } else if (node.type === 'interactive-list') {
        const sections = (node.data.interactive as any)?.action?.sections || [];
        sections.forEach((section: any) => {
          section.rows?.forEach((row: any) => {
            optionTitleMap.set(row.id, row.title);
          });
        });
      }

      outgoingEdges.forEach((edge) => {
        if (edge.sourceHandle?.startsWith('branch-')) {
          const optionId = edge.sourceHandle.replace('branch-', '');
          const targetNode = nodes.find((n) => n.id === edge.target);
          const targetStepId = (targetNode?.data.stepId as string) || targetNode?.id || '';

          // ✅ Use button TITLE instead of internal ID
          const optionTitle = optionTitleMap.get(optionId) || optionId;

          conditions.push({
            value: optionTitle,
            targetStep: targetStepId,
          });
        }
      });
    }

    // Transform fieldType: We use "interactive-button"/"interactive-list", API expects "interactive" + type
    let fieldType = (node.data.questionType as string) || node.type || 'text';
    let interactiveData: any = undefined;

    if (fieldType === 'interactive-button' || fieldType === 'interactive-list') {
      fieldType = 'interactive'; // API uses generic "interactive" fieldType

      // ✅ FIXED: Build complete interactive object with proper structure
      const interactive = node.data.interactive as QuestionNodeData['interactive'];
      if (interactive) {
        interactiveData = {
          type: interactive.type,
          header: interactive.header || { type: 'text', text: 'Choose an option' },
          body: { text: (node.data.question as string) || interactive.body?.text || '' },
          action: interactive.action || (interactive.type === 'button' ? { buttons: [] } : { button: 'View Options', sections: [] }),
        };

        // Add footer if it exists
        if (interactive.footer) {
          interactiveData.footer = interactive.footer;
        }
      }
    }

    // 🔥 TEMPLATE: Build minimal step with top-level `template` field (matches backend format)
    if (fieldType === 'template') {
      const tplData = node.data.templateData as QuestionNodeData['templateData'];
      const templateId = tplData?.templateId || '';
      const cleanedMeta = { ...(node.data.metadata as any) };
      delete cleanedMeta.nextStep;

      const botStep: BotStep = {
        id: (node.data.stepId as string) || node.id,
        fieldType: 'template',
        message: null,
        metadata: {
          ...cleanedMeta,
          ...(tplData?.templateName ? { templateName: tplData.templateName } : {}),
          nextStep: nextStepId,
        },
        // @ts-ignore — backend expects top-level `template` field with the ID string
        template: templateId,
        conditions: conditions.length > 0 ? conditions : undefined,
      };

      groupsMap.get(groupId)!.push(botStep);
      return; // Skip the generic step builder below
    }

    // 🔥 CRITICAL: For interactive types, message MUST be null (content is in interactive.body.text)
    const isInteractiveType = fieldType === 'interactive';

    // ✅ FIXED: Provide sensible default message for non-interactive nodes when user leaves it empty
    const getDefaultMessage = (type: string): string => {
      switch (type) {
        case 'text': return 'Please enter your response';
        case 'email': return 'Please enter your email address';
        case 'phone': return 'Please enter your phone number';
        case 'number': return 'Please enter a number';
        case 'location': return 'Please share your location';
        default: return 'Please respond';
      }
    };

    // ✅ FIX: Clear stale nextStep from old metadata before rebuilding
    const cleanedMetadata = { ...(node.data.metadata as any) };
    delete cleanedMetadata.nextStep;

    const botStep: BotStep = {
      id: (node.data.stepId as string) || node.id,
      fieldType: fieldType,
      // ✅ FIXED: For interactive types, message goes in interactive.body.text, not here
      // For non-interactive types, use a default message if empty
      message: isInteractiveType ? null : ((node.data.question as string) || getDefaultMessage(fieldType)),
      validation: (node.data.validation as any) || {
        required: node.data.required || false,
        minLength: null,
        maxLength: null,
      },
      interactive: interactiveData,
      metadata: {
        ...cleanedMetadata,
        nextStep: nextStepId, // ✅ Explicit: string ID if connected, null if intentionally disconnected
      },
      conditions: conditions.length > 0 ? conditions : undefined,
      options: (node.data.options as any) || undefined,
    };

    groupsMap.get(groupId)!.push(botStep);
  });

  // Convert map to groups array with proper structure
  const groups: BotGroup[] = Array.from(groupsMap.entries()).map(
    ([groupId, steps], index) => {
      // Find any node from this group to get groupName
      const sampleNode = questionNodes.find(n => nodeToGroupMap.get(n.id) === groupId);
      const groupName = (sampleNode?.data.groupName as string) || `Group ${index + 1}`;

      return {
        id: groupId,
        groupName: groupName,
        required: false, // Default - can be customized later
        conditions: {}, // Group-level conditions (rarely used)
        steps,
      };
    }
  );

  logger.log('[FlowConverter] Conversion complete', {
    groupCount: groups.length,
    keywords: triggerCondition.keywords.length,
  });

  return { groups, triggerCondition };
}

/**
 * Helper: Generate unique IDs
 */
let idCounter = 1;
export function generateNodeId(): string {
  return `node-${Date.now()}-${idCounter++}`;
}

export function generateGroupId(): string {
  return `group-${Date.now()}-${idCounter++}`;
}
